import "./style.scss";

import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Skeleton, Tooltip } from "@mui/material";
import Pdf from "assets/img/svg/pdf";
import AddDocument from "components/shared/AddDocument";
import InlineButton from "components/shared/InlineButton";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Agreement, AgreementDocument } from "store/agreementsStore";
import { deleteAgreementDocument, downloadAgreementDocument, postAgreementDocument } from "store/agreementsStore";
import { useAppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";

/**
 * Attached documents component, used in the right panel of contract validation page
 */
interface IAttachedDocuments {
    /**
     * Agreement data
     */
    agreement: Agreement | null;
    /**
     * Is the agreement creator
     */
    isOwner: boolean;
    /**
     * Is agreement status === "draft"
     */
    isDraft: boolean;
    /**
     * Agreement attached documents
     */
    agreementDocuments: AgreementDocument[];
    /**
     * Fetch status for agreement documents
     */
    agreementDocumentsStatus: TFetchStatus;
}

const AttachedDocuments = ({
    agreement,
    isOwner,
    isDraft,
    agreementDocuments,
    agreementDocumentsStatus,
}: IAttachedDocuments) => {
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const handleSendDocument = (file: { formData: FormData }) => {
        const { formData } = file;
        dispatch(postAgreementDocument({ agreementId: agreement?.id as number, formData }));
    };

    const handleDeleteDocument = (documentId: number, e: any) => {
        e.stopPropagation();
        dispatch(deleteAgreementDocument({ documentId, agreementId: agreement?.id as number }));
    };

    const handleDownloadDocument = (file: AgreementDocument) => {
        dispatch(downloadAgreementDocument({ agreementId: agreement?.id as number, documentId: file.id, file }));
    };

    return (
        <>
            {agreementDocumentsStatus !== "succeeded" ? (
                <Skeleton />
            ) : (
                <div className="contract-validation-edit-right-panel-documents">
                    <BodyHighlight color="black">
                        {t("ContractValidationEdit.AttachedDocuments")} ({agreementDocuments?.length ?? 0})
                    </BodyHighlight>
                    {isOwner && isDraft && (
                        <InlineButton
                            onClick={() => {
                                setShowDocumentModal(true);
                            }}
                            plusIconStyle={{ width: 14 }}
                        >
                            <SmallBody color="info">{t("ContractValidationEdit.AddDocument")}</SmallBody>
                        </InlineButton>
                    )}
                    {agreementDocuments?.length > 0 && (
                        <div className="attached-documents-list">
                            {agreementDocuments?.map((file) => {
                                const documentName = `${file.name}.${file.mimetype.split("/").pop()}`;
                                return (
                                    <div
                                        className="document"
                                        key={file.storageId}
                                        onClick={() => handleDownloadDocument(file)}
                                    >
                                        <div className="document-left">
                                            <Pdf />
                                            <SmallBody color="grey" style={{ fontSize: "11px" }}>
                                                {documentName}
                                            </SmallBody>
                                        </div>
                                        {isOwner && isDraft && (
                                            <Tooltip title={t("ContractValidationEdit.Delete")}>
                                                <ClearOutlinedIcon
                                                    className="clear-icon"
                                                    onClick={(e) => handleDeleteDocument(file.id, e)}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="contract-validation-edit-right-panel-dividers" />

                    {/**
                     * ADD DOCUMENTS MODAL
                     */}
                    <AddDocument
                        isModalOpen={showDocumentModal && isOwner}
                        onClose={() => setShowDocumentModal(false)}
                        onFileAdd={handleSendDocument}
                        modalSubtitle={t("ContractValidationEdit.DocumentWillBeAttachedToContract")}
                    />
                </div>
            )}
        </>
    );
};

export default AttachedDocuments;
